import rawData from '../data/rawMaterialData';

/**
 * Shared utility for building / adjusting the layers array when the ply value
 * changes.  Instead of resetting every layer to blanks, this function:
 *
 *   1. Determines the layer‑type sequence for the new ply value
 *      (top → [flute, face]… → flute → bottom).
 *   2. Copies across any previously‑set values for each layer type so that
 *      existing face / flute / top / bottom material settings are preserved.
 *   3. Only creates blank layers when there is no prior data to copy from.
 *   4. Returns the new layers array together with matching gsm / bf arrays
 *      (pre‑populated with dropdown options based on each layer's materialType).
 *
 * @param {number}  newPly       – The new ply count (3 / 5 / 7 / 9).
 * @param {Array}   oldLayers    – The current layers state (may be empty).
 * @param {Object}  layerObjTpl  – Blank layer template, e.g.
 *                                  { layerId:0, layerType:"", materialType:"", gsm:"", bf:"", ff:"" }
 * @returns {{ layersArray: Array, gsmArray: Array, bfArray: Array }}
 */
export function buildLayersForPly(newPly, oldLayers = [], layerObjTpl) {
  // ── 1. Build the ordered type‑sequence for the requested ply ──────────
  const typeSequence = []; // e.g. ['top','flute','face','flute','bottom']
  if (newPly % 2 !== 0) {
    typeSequence.push("top");
    for (let i = 1; i <= newPly - 2; i++) {
      typeSequence.push(i % 2 === 0 ? "face" : "flute");
    }
    typeSequence.push("bottom");
  }

  // ── 2. Index old layers by type so we can pull existing values ────────
  //    We keep separate queues per type so that when going from 7→9 ply the
  //    first two flute layers keep their individual settings.
  const oldByType = {};
  (oldLayers || []).forEach((l) => {
    if (!l || !l.layerType) return;
    if (!oldByType[l.layerType]) oldByType[l.layerType] = [];
    oldByType[l.layerType].push({ ...l });
  });

  // ── Face ↔ Bottom fallback ───────────────────────────────────────────
  //    In Basic mode the "Face Layer" section also writes to the "bottom"
  //    layer (e.g. in 3‑ply: top / flute / bottom – there is no explicit
  //    "face" layer).  When the user later switches to 5 / 7 / 9 ply the
  //    new type‑sequence includes "face" layers but `oldByType` has none.
  //    We fall back to "bottom" data for "face" (and vice‑versa) so the
  //    material choices carry over.
  const hasData = (arr) => arr && arr.length > 0 && arr.some((l) => l.materialType);

  if (!hasData(oldByType["face"]) && hasData(oldByType["bottom"])) {
    oldByType["face"] = oldByType["bottom"].map((l) => ({ ...l }));
  }
  if (!hasData(oldByType["bottom"]) && hasData(oldByType["face"])) {
    oldByType["bottom"] = oldByType["face"].map((l) => ({ ...l }));
  }

  // Consumption cursors – how many of each type we've already reused.
  const cursor = {};

  // Helper: find the "template" values (most‑recently‑used non‑blank layer)
  // for a given type so that newly‑added layers inherit sensible defaults.
  const getTemplate = (type) => {
    const pool = oldByType[type];
    if (!pool || pool.length === 0) return null;
    // Prefer the last non‑blank layer
    for (let i = pool.length - 1; i >= 0; i--) {
      if (pool[i].materialType) return pool[i];
    }
    return pool[pool.length - 1]; // all blank – return last anyway
  };

  // ── 3. Build the new layers array ────────────────────────────────────
  const layersArray = [];
  const gsmArray = [];
  const bfArray = [];

  typeSequence.forEach((type, idx) => {
    if (!cursor[type]) cursor[type] = 0;
    const pool = oldByType[type] || [];
    const ci = cursor[type];

    let layer;
    if (ci < pool.length) {
      // Reuse existing layer data (preserves materialType, gsm, bf, ff)
      layer = { ...pool[ci] };
    } else {
      // No old layer to reuse – try to clone the template for this type
      const tpl = getTemplate(type);
      if (tpl && tpl.materialType) {
        layer = { ...tpl };
      } else {
        layer = { ...layerObjTpl };
      }
    }

    // Always correct the id & type to match the new position
    layer.layerId = idx + 1;
    layer.layerType = type;

    layersArray.push(layer);

    // Pre‑populate the gsm / bf dropdown‑option arrays from the layer's
    // materialType so that selectors render with correct options immediately
    // (avoids a blank‑dropdown flash on the first render after ply change).
    const mt = layer.materialType;
    gsmArray.push(mt && rawData.materialGsm[mt] ? rawData.materialGsm[mt] : []);
    bfArray.push(mt && rawData.materialBf[mt] ? rawData.materialBf[mt] : []);

    cursor[type] = ci + 1;
  });

  return { layersArray, gsmArray, bfArray };
}

/**
 * Format a layer object into a material display string.
 * Output: ALL CAPS, no spaces — matches RawMaterialMaster naming convention.
 * @param {Object} layer - { materialType, gsm, bf, ff }
 * @returns {string} e.g. "KRAFT-140-18BF" or "SEMI-KRAFT-100-18BF"
 */
const MATERIAL_TYPE_MAP = {
  semi: "SEMI-KRAFT",
  "semi kraft": "SEMI-KRAFT",
  "semi-kraft": "SEMI-KRAFT",
};

export function displayMaterialType(type) {
  if (!type) return "SEMI-KRAFT";
  const mapped = MATERIAL_TYPE_MAP[type.toLowerCase()];
  return mapped || type.toUpperCase().replace(/\s+/g, "-");
}

export function formatLayerMaterial(layer) {
  if (!layer) return "UNKNOWN";
  const type = displayMaterialType(layer.materialType).toUpperCase().replace(/\s+/g, "-");
  const gsm = layer.gsm || "";
  const bf = layer.bf ? `-${layer.bf}BF` : "";
  return `${type}-${gsm}${bf}`;
}
